import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { GiftIcon } from 'lucide-react';
import { btnSecondary, inputCls } from '../../ui/styles';
import { euroCents } from './checkoutRules';
import { fromIso, fullDate } from '../../core/format/dates';
import type { VoucherControl } from './useVoucher';

/**
 * "Heb je een cadeaubon?" — eerst een rustige link, dan het veld. Controleren boekt nog niets af;
 * dat gebeurt pas bij bestellen. Een bon die alleen voor behandelingen geldt, krijgt de uitleg
 * van de server ("Deze cadeaubon is voor behandelingen in de salon").
 */
export function VoucherField({
  v
}: {
  v: VoucherControl;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const errorId = `${id}-error`;
  const inputRef = useRef<HTMLInputElement>(null);
  // Na "Heb je een cadeaubon?" meteen in het veld, zodat ze kan typen.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);
  if (v.applied) {
    return <div className="mt-6 border-t border-line pt-4 text-[14px]" data-id="element-7357" data-mp="s9w9minzi0fq">
        <div className="flex items-start justify-between gap-4" data-id="element-7358" data-mp="261stmsnhtii2">
          <p className="min-w-0 text-ink" data-id="element-7359" data-mp="212pqjdr2pmcq">
            <GiftIcon className="mr-1.5 inline h-4 w-4 -translate-y-px text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7360" data-mp="2140r74p82dzm" />
            Cadeaubon ···{v.last4}
            <span className="block text-[13px] text-muted" data-id="element-7361" data-mp="2b043v4hp9v0t">
              {v.leftCents > 0 ? `Daarna staat er nog ${euroCents(v.leftCents / 100)} op, te gebruiken tot ${fullDate(fromIso(v.applied.validUntil))}.` : 'Daarna is de bon helemaal gebruikt.'}
            </span>
          </p>
          <button type="button" onClick={v.remove} className="shrink-0 text-[13px] text-ink underline underline-offset-4 hover:text-clay" data-id="element-7362" data-mp="2yowuclfjzao1">
            Weghalen<span className="sr-only" data-id="element-7363" data-mp="2tt7q44sl95le">: cadeaubon</span>
          </button>
        </div>
      </div>;
  }
  if (!open) {
    return <p className="mt-6 border-t border-line pt-4" data-id="element-7364" data-mp="32sb978rr9kzw">
        <button type="button" onClick={() => setOpen(true)} className="text-[14px] text-ink underline underline-offset-4 hover:text-clay" aria-expanded="false" data-id="element-7365" data-mp="32pp1980yd3h5">
          Heb je een cadeaubon?
        </button>
      </p>;
  }
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void v.apply();
  };
  return <form onSubmit={submit} className="mt-6 border-t border-line pt-4" noValidate data-id="element-7366" data-mp="2e5a4inwp4tcs">
      <label htmlFor={id} className="mb-1.5 block text-[14px] text-ink" data-id="element-7367" data-mp="2e7d0dokj5gpf">
        Cadeaubon
      </label>
      <div className="flex gap-2" data-id="element-7368" data-mp="2o3ptak893qj4">
        <input id={id} value={v.input} onChange={e => v.setInput(e.target.value)} placeholder="BB-XXXX-XXXX-XXXX" autoComplete="off" autoCapitalize="characters" spellCheck={false} ref={inputRef} aria-invalid={v.error ? true : undefined} aria-describedby={v.error ? errorId : undefined} className={`${inputCls} min-w-0 flex-1 font-mono tracking-wide`} data-id="element-7369" data-mp="2o2n6ane1u88h" />
        <button type="submit" disabled={v.checking || !v.input.trim()} className={`${btnSecondary} shrink-0`} data-id="element-7370" data-mp="v6kltxevv4z6">
          {v.checking ? 'Controleren…' : 'Gebruiken'}
        </button>
      </div>
      <p id={errorId} role="status" aria-live="polite" className={`mt-1.5 text-[13px] ${v.error ? 'text-clay' : 'text-muted'}`} data-id="element-7371" data-mp="lgrkrqcda896">
        {v.error ?? 'In de webshop te gebruiken als de bon ook voor producten geldt.'}
      </p>
    </form>;
}